import { useParams } from "react-router-dom";
import AppHeader from "../components/appheader";
import "./question.scss";
import LoadError from "../components/error";
import { AppDataContext, ctxDefinition } from "../components/ctx";
import { SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { ApiEndpoint, BrandColors } from "../components/utils";
import axios from "axios";
import { useQuery } from "react-query";
import Loader from "../components/loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  Alert,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
} from "@mui/material";
import {
  BrandButton,
  BrandButtonGray,
  BrandButtonWhite,
} from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import { useNavigate } from "react-router-dom";
import Calculator from "../components/form/calculator";
import FooterQuestionIndex from "../components/footerQuestionIndex";
import NetworkError from "../components/networkError";
import Resources from "../components/form/resources";

interface QuestionDefinition {
  success: boolean;
  questionId: string;
  type: string;
  description: any;
  options: string[];
  isMath: boolean;
  questions: any;
  isPassage?: boolean;
  passage: any;
}

const Question = () => {
  const { userData, setUserData, config } = useContext(
    AppDataContext
  ) as ctxDefinition;
  const { questionid } = useParams();
  const [questionData, setQuestionData] = useState<QuestionDefinition>();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [radioOption, setRadioOption] = useState<null | string>();
  const [snackError, setSnackError] = useState(false);
  const [snackSaved, setSnackSaved] = useState(false);
  const vertical = "bottom";
  const horizontal = "center";
  const [errorMsg, setErrorMsg] = useState("");
  const [fillInValue, setFillinValue] = useState("");
  const previousAnswer = useRef<string | boolean>("");
  const answered = useRef(false);
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState("0");
  const [loading, setLoading] = useState(false);
  const [showFooterModal, setShowFooterModal] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [hasError, setHasError] = useState(false);
  const allowedChars = new RegExp(/^[-]?[0-9./]*$/gi);
  const [networkError, setNetworkError] = useState(false);

  const { isLoading, error, refetch } = useQuery(
    "questionPageData",
    async () => {
      if (questionid) {
        try {
          const resp = await axios.get(
            ApiEndpoint +
              "wp-json/firstchoiceprep/v1/getExamQuestion?token=" +
              userData.token +
              "&questionId=" +
              questionid
          );
          if (!resp || !resp.data.success) {
            setHasError(true);
            return;
          }
          setQuestionData(resp.data);
        } catch (e) {
          // handle network error
          setNetworkError(true);
        }
      } else {
        return null;
      }
    }
  );

  const submitAnswer = () => {
    setLoading(true);
    if (questionData?.type === "multiple") {
      if (answered.current !== false && !radioOption) {
        gotoNextQuestion();
        return;
      }
      if (radioOption) {
        updateUserQuestionData(parseInt(radioOption));
        gotoNextQuestion();
      } else {
        setLoading(false);
        setErrorMsg(
          "Please select an answer before submitting or skip this question."
        );
        setSnackError(true);
      }
    } else if (questionData?.type === "input") {
      if (answered.current !== false && fillInValue !== "") {
        updateUserQuestionData(fillInValue);
        gotoNextQuestion();
        return;
      }
      if (fillInValue === "") {
        setLoading(false);
        setErrorMsg(
          "Please fill in an answer before submitting or skip this question."
        );
        setSnackError(true);
        return;
      }
    }
  };

  const gotoNextQuestion = async () => {
    setLoading(true);
    resetQuestion();
    const curSection =
      userData.examAnswers[userData.currentSectionId].questions;
    if (questionIndex >= curSection.length - 1) {
      setLoading(false);
      navigate("/section-complete", { replace: true });
    } else {
      setQuestionIndex(questionIndex + 1);
      const nextQId =
        userData.examAnswers[userData.currentSectionId].questions[
          questionIndex + 1
        ].questionId;

      try {
        const resp = await axios.post(
          ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
          {
            token: userData.token,
            fields: ["lastQuestionAnswered"],
            values: [nextQId],
          }
        );
        if (!resp || !resp.data.success) {
          setHasError(true);
          return;
        }
        setSnackSaved(true);
        navigate("/question/" + nextQId, { replace: true });
      } catch (e) {
        setNetworkError(true);
      }
    }
  };

  const checkIfAnswered = () => {
    setFillinValue("");
    const currentSectionQuestions =
      userData.examAnswers[userData.currentSectionId].questions;
    currentSectionQuestions.forEach((item: any, index: number) => {
      if (questionid === item.questionId.toString()) {
        previousAnswer.current = item.value;
        if (item.value !== null) {
          answered.current = true;
          if (questionData?.type === "input") {
            setFillinValue(item.value);
          } else {
            setRadioOption(item.value);
          }
        } else {
          answered.current = false;
        }
      }
    });
    setLoading(false);
  };

  const updateUserQuestionData = async (val: any) => {
    let section: number = userData.currentSectionId;
    let tempQuestionIndex: number = -1;
    const currentSectionQuestions =
      userData.examAnswers[userData.currentSectionId].questions;
    currentSectionQuestions.forEach((item: any, index: number) => {
      // eslint-disable-next-line eqeqeq
      if (questionid == item.questionId) tempQuestionIndex = index;
    });
    let temp = { ...userData };
    temp.examAnswers[section].questions[tempQuestionIndex].value = val;

    const resp = await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/submitAnswer",
      {
        token: userData.token,
        questionId: questionid,
        answer: val,
      }
    );
    if (!resp || !resp.data.success) {
      setHasError(true);
      return;
    }

    setUserData(temp);
  };

  const closeSnack = () => {
    setSnackError(false);
    setSnackSaved(false);
  };

  const resetQuestion = () => {
    setFillinValue("");
    previousAnswer.current = "";
    setRadioOption(null);
    setCurrentQuestionNumber("");
  };

  const gotoQuestion = (questionId: string) => {
    setCurrentQuestionNumber(questionId);
    navigate("/question/" + questionId, { replace: true });
  };

  const gotoPreviousQuestion = () => {
    const currQuestion =
      userData.examAnswers[userData.currentSectionId].questions[
        questionIndex - 1
      ];
    navigate("/question/" + currQuestion.questionId, { replace: true });
  };

  const checkInputAnswer = (val: SetStateAction<string>) => {
    const inputVal = val as string;
    const checkValid = inputVal.match(allowedChars);
    if (checkValid || inputVal === "") {
      setFillinValue(val);
      answered.current = true;
    }
  };

  useEffect(() => {
    // setLoading(false);
    if (!questionData || !userData) return;
    if (questionData) {
      checkIfAnswered();
      const currentSectionQuestions =
        userData.examAnswers[userData.currentSectionId].questions;

      setTotalQuestions(currentSectionQuestions.length);
      currentSectionQuestions.forEach((item: any, index: number) => {
        // eslint-disable-next-line eqeqeq
        if (questionid == item.questionId) setQuestionIndex(index);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  useEffect(() => {
    if (questionid) {
      refetch();
    }
  }, [questionid, refetch]);

  const updateSubmitEnabledBtn = (isEnabled: boolean) => {
    setSubmitEnabled(isEnabled);
  };

  if (networkError) return <NetworkError open={true} />;

  if (!userData || !questionid) return <LoadError />;
  if (isLoading || loading) return <Loader />;
  if (error) return <LoadError />;

  return (
    <>
      <div className="questionPage">
        <div>
          <AppHeader
            timer={true}
            checkTimer={true}
            exposeTimer={updateSubmitEnabledBtn}
          />
          <div className="questionHeader">
            <h1>
              {config.questionSections[userData.currentSectionId].includes(
                "2a"
              ) ||
              config.questionSections[userData.currentSectionId].includes("2b")
                ? config.questionSections[userData.currentSectionId].substring(
                    0,
                    config.questionSections[userData.currentSectionId].length -
                      1
                  )
                : config.questionSections[userData.currentSectionId]}
            </h1>
            <p>
              Question {questionIndex + 1} of {totalQuestions}
            </p>
          </div>
          <div
            className={
              questionData?.isPassage
                ? "questionContent passage"
                : "questionContent"
            }
          >
            {questionData?.isPassage && (
              <div
                className="passageText"
                dangerouslySetInnerHTML={{
                  __html: questionData?.passage,
                }}
              />
            )}
            <div className="answerWrapper">
              <div className="questionHolder">
                <div className="questionCount">{questionIndex + 1})</div>
                <div
                  className="questionText"
                  dangerouslySetInnerHTML={{
                    __html: questionData?.description,
                  }}
                />
              </div>

              <div className="questionAnswersHolder">
                {questionData?.type === "multiple" && (
                  <div className="multipleAnswers">
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="question-options"
                        name="radio-options"
                        key="radio-options"
                      >
                        {questionData?.options.map((item: any, index: any) => {
                          return (
                            <>
                              <FormControlLabel
                                key={index}
                                value={index}
                                control={
                                  <Radio
                                    sx={{
                                      color: BrandColors.brandGreen,
                                      "&.Mui-checked": {
                                        color: BrandColors.brandGreen,
                                      },
                                    }}
                                    onChange={(evt) => {
                                      previousAnswer.current = evt.target.value;
                                      setRadioOption(evt.target.value);
                                    }}
                                    checked={
                                      // eslint-disable-next-line eqeqeq
                                      index == previousAnswer.current &&
                                      previousAnswer.current !== null
                                        ? true
                                        : false
                                    }
                                  />
                                }
                                label={item}
                              />
                            </>
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                  </div>
                )}
                {questionData?.type === "input" && (
                  <div className="inputHolder">
                    <BrandInput
                      onChange={(evt) => {
                        checkInputAnswer(evt.target.value);
                      }}
                      id="filled-basic"
                      label="Answer Here"
                      variant="filled"
                      value={fillInValue}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: "flex" }}>
                <div className="submitAnswerHolder">
                  <BrandButton onClick={submitAnswer} disabled={!submitEnabled}>
                    SUBMIT ANSWER
                  </BrandButton>
                  <BrandButtonWhite
                    onClick={gotoNextQuestion}
                    style={{ marginTop: "10px" }}
                    disabled={!submitEnabled}
                  >
                    SKIP QUESTION
                  </BrandButtonWhite>
                </div>
              </div>
            </div>
          </div>

          <Snackbar
            open={snackError}
            autoHideDuration={6000}
            onClose={closeSnack}
            anchorOrigin={{ vertical, horizontal }}
          >
            <Alert severity="error">{errorMsg}</Alert>
          </Snackbar>
          <Snackbar
            open={snackSaved}
            autoHideDuration={2000}
            onClose={closeSnack}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          >
            <Alert severity="success">Answer Saved</Alert>
          </Snackbar>
        </div>
        {questionData?.isMath && <Calculator />}
        {(userData.currentSectionId === 6 ||
          userData.currentSectionId === 8 ||
          userData.currentSectionId === 9) && <Resources />}
      </div>
      <div className="questionNav">
        <div className="holder">
          {showFooterModal && (
            <FooterQuestionIndex
              data={userData.examAnswers[userData.currentSectionId].questions}
              onChange={gotoQuestion}
              onToggle={setShowFooterModal}
            />
          )}
          <div className="nav-buttons">
            <BrandButtonGray
              style={{ marginTop: "10px" }}
              onClick={() => setShowFooterModal(!showFooterModal)}
            >
              Go to Specific Question
            </BrandButtonGray>
            {questionIndex > 0 && (
              <BrandButton
                onClick={gotoPreviousQuestion}
                style={{ marginTop: "10px" }}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Previous QUESTION
              </BrandButton>
            )}
          </div>
        </div>
      </div>
      <NetworkError open={hasError} />
    </>
  );
};

export default Question;
